/**
 * useSidebarCollapse
 *
 * Persisted collapsed/expanded state for the app's desktop sidebar. Shared by
 * both layout shells (`AppShell` for the main app, `AdminLayout` for
 * `/admin/*`) so a user's preference doesn't reset when crossing between
 * them.
 *
 * @fileoverview Persisted sidebar-collapse hook
 */

import { useCallback, useState } from 'react';

/** localStorage key. Shared across layouts on purpose (see fileoverview). */
export const SIDEBAR_COLLAPSE_STORAGE_KEY = 'xacto.sidebar.collapsed';

const readStored = (initial: boolean): boolean => {
  try {
    const raw = localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY);
    return raw === null ? initial : raw === '1';
  } catch {
    // Safari private mode, storage disabled in an iframe, etc. — fall back
    // to the in-memory default rather than throwing.
    return initial;
  }
};

const persist = (value: boolean): void => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Best-effort only; the collapsed state still works for this session.
  }
};

/**
 * Whether a keyboard event originated from somewhere the user is typing.
 * Used by both layout shells to keep the Ctrl/Cmd+B collapse shortcut from
 * firing while editing text (e.g. the transactions search box).
 */
export const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
};

export interface UseSidebarCollapseResult {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (value: boolean) => void;
}

/**
 * @param initial - Default when nothing is stored yet (default: expanded).
 */
export function useSidebarCollapse(initial = false): UseSidebarCollapseResult {
  // Read synchronously in the useState initializer so the very first paint
  // after a refresh already reflects the stored preference — avoids a
  // flash of the wide sidebar before a useEffect could correct it.
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => readStored(initial));

  const setCollapsed = useCallback((value: boolean) => {
    setIsCollapsed(value);
    persist(value);
  }, []);

  // Deliberately derived from the current value rather than using the
  // functional `setIsCollapsed(prev => ...)` form: the localStorage write is
  // a side effect, and React may invoke a state updater more than once per
  // update (StrictMode double-invokes them in development), so updaters have
  // to stay pure. The cost is that `toggle`'s identity changes with
  // `isCollapsed` — harmless, and it keeps the keyboard-shortcut effect that
  // depends on it from ever closing over a stale value.
  const toggle = useCallback(() => {
    setCollapsed(!isCollapsed);
  }, [isCollapsed, setCollapsed]);

  return { isCollapsed, toggle, setCollapsed };
}

export default useSidebarCollapse;
